import { useQuery } from '@tanstack/react-query';
import {
  Laptop, LogIn, LogOut, Mail, MapPin, Briefcase, Building2, IdCard, History, PackageX,
} from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getEmpleadoDetalle } from '@/services/api/empleados.api';
import { cn } from '@/lib/utils';
import type { EmpleadoAcceso } from '@/types/dominio';

interface Props {
  empleadoId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Cierra este diálogo y abre el carnet del empleado. */
  onVerCarnet?: (id: string) => void;
}

function InfoRow({ icon: Icon, label, value }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | null;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground truncate">{value || '—'}</p>
      </div>
    </div>
  );
}

function AccesoTipo({ tipo }: { tipo: EmpleadoAcceso['tipo_movimiento'] }) {
  const isEntrada = tipo === 'ENTRADA';
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1',
      isEntrada ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-amber-50 text-amber-800 ring-amber-200',
    )}>
      {isEntrada ? <LogIn className="h-3 w-3" /> : <LogOut className="h-3 w-3" />}
      {isEntrada ? 'Entrada' : 'Salida'}
    </span>
  );
}

export function EmpleadoDetalleDialog({ empleadoId, open, onOpenChange, onVerCarnet }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['empleado-detalle', empleadoId],
    queryFn: () => getEmpleadoDetalle(empleadoId as string),
    enabled: open && !!empleadoId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isLoading || !data ? 'Cargando…' : data.empleado.nombres}
          </DialogTitle>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* ── Datos del empleado ──────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border bg-muted/30 p-4">
              <InfoRow icon={IdCard} label="Documento" value={data.empleado.documento} />
              <InfoRow icon={Briefcase} label="Cargo" value={data.empleado.cargo} />
              <InfoRow icon={Building2} label="Área" value={data.empleado.area} />
              <InfoRow icon={Mail} label="Email" value={data.empleado.email} />
              <InfoRow icon={MapPin} label="Dirección" value={data.empleado.direccion} />
            </div>

            {onVerCarnet && data.empleado.activo && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  // Un solo diálogo a la vez: cerramos el detalle antes de abrir el carnet.
                  onOpenChange(false);
                  onVerCarnet(data.empleado.id_empleado);
                }}
              >
                <IdCard className="h-4 w-4" />
                Generar carnet con QR
              </Button>
            )}

            {/* ── Equipos asignados ───────────────────────────────────────── */}
            <section>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                <Laptop className="h-4 w-4 text-primary-600" />
                Herramientas asignadas ({data.equipos.length})
              </h3>
              {data.equipos.length === 0 ? (
                <div className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                  <PackageX className="h-4 w-4" />
                  Sin herramientas corporativas asignadas
                </div>
              ) : (
                <ul className="divide-y divide-border rounded-lg border">
                  {data.equipos.map((e) => (
                    <li key={e.id_item_maestro} className="flex items-center gap-3 px-3 py-2">
                      <Laptop className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{e.nombre ?? e.serial_global}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {[e.tipo?.toLowerCase(), `serial ${e.serial_global}`].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ── Accesos recientes ───────────────────────────────────────── */}
            <section>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                <History className="h-4 w-4 text-primary-600" />
                Accesos recientes
              </h3>
              {data.accesos.length === 0 ? (
                <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                  Sin movimientos de acceso registrados
                </div>
              ) : (
                <ul className="divide-y divide-border rounded-lg border">
                  {data.accesos.map((a) => (
                    <li key={a.id_movimiento} className="flex items-center gap-3 px-3 py-2">
                      <AccesoTipo tipo={a.tipo_movimiento} />
                      <span className="flex-1 text-xs text-muted-foreground truncate">
                        {a.equipo ? `Equipo ${a.equipo}` : 'Solo persona'}
                      </span>
                      <span className="text-xs tabular text-muted-foreground whitespace-nowrap">
                        {new Date(a.fecha_hora).toLocaleString('es-CO', {
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
